import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiProgress from "components/VuiProgress";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import LineChart from "examples/Charts/LineCharts/LineChart";

import { getSummary, getInsights } from "services/api";
import { IoSparkles, IoTrendingUp, IoTrendingDown, IoRemove } from "react-icons/io5";

const INSIGHT_STYLES = {
  saving: { bg: "rgba(0,117,255,0.1)", border: "rgba(0,117,255,0.3)", iconColor: "#0075FF" },
  warning: { bg: "rgba(231,76,60,0.1)", border: "rgba(231,76,60,0.3)", iconColor: "#E31A1A" },
  positive: { bg: "rgba(1,181,116,0.1)", border: "rgba(1,181,116,0.3)", iconColor: "#01B574" },
  tip: { bg: "rgba(255,181,71,0.1)", border: "rgba(255,181,71,0.3)", iconColor: "#FFB547" },
};

function Insights() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(true);

  useEffect(() => {
    getSummary().then(setData);
    getInsights().then((d) => {
      setInsights(d);
      setLoadingInsights(false);
    });
  }, []);

  const refreshInsights = () => {
    setLoadingInsights(true);
    getInsights().then((d) => {
      setInsights(d);
      setLoadingInsights(false);
    });
  };

  if (!data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3} display="flex" justifyContent="center" minHeight="60vh">
          <VuiTypography color="white" variant="h5">Loading...</VuiTypography>
        </VuiBox>
      </DashboardLayout>
    );
  }

  const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
  const insightsList = insights?.insights || [];
  const predictions = insights?.predictions || [];
  const isAI = insights?.ai || false;

  // Balance trend from daily data
  const dailyTrend = data.daily_trend || [];
  let runningBalance = 0;
  const balanceData = dailyTrend.map((d) => {
    runningBalance -= d.amount;
    return runningBalance;
  });
  const balanceLabels = dailyTrend.map((d) => {
    const p = d.date.split("-");
    return p.length >= 3 ? `${p[2]}/${p[1]}` : "";
  });

  const balanceLineData = [{ name: "Balance Impact", data: balanceData }];
  const balanceLineOptions = {
    chart: { toolbar: { show: false } },
    tooltip: { theme: "dark", y: { formatter: (v) => `Rs. ${Number(v).toLocaleString()}` } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      categories: balanceLabels,
      labels: { style: { colors: "#c8cfca", fontSize: "9px" }, rotate: -45, rotateAlways: true },
      axisBorder: { show: false }, axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#c8cfca", fontSize: "10px" },
        formatter: (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
      },
    },
    legend: { show: false },
    grid: { strokeDashArray: 5, borderColor: "#56577A" },
    fill: {
      type: "gradient",
      gradient: { shade: "dark", type: "vertical", shadeIntensity: 0, opacityFrom: 0.3, opacityTo: 0, stops: [] },
    },
    colors: ["#7928CA"],
    markers: { size: 0 },
  };

  // Budget goals
  const budgetMonthly = 400000;
  const budgetWeekly = 100000;
  const goals = [
    { name: "Monthly Budget", current: data.month_spend, target: budgetMonthly, color: "info" },
    { name: "Weekly Budget", current: data.week_spend, target: budgetWeekly, color: "info" },
    { name: "Save 20% Income", current: Math.max(0, (data.month_income || 0) - data.month_spend), target: (data.month_income || 1) * 0.2, color: "success" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* AI Insights Header */}
        <VuiBox mb={3}>
          <Card sx={{ p: 3, background: "linear-gradient(135deg, rgba(121,40,202,0.15) 0%, rgba(0,117,255,0.15) 100%)" }}>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="12px">
              <VuiBox display="flex" alignItems="center" gap="12px">
                <VuiBox
                  display="flex" justifyContent="center" alignItems="center"
                  sx={{ width: "48px", height: "48px", borderRadius: "12px", background: "linear-gradient(135deg, #7928CA, #0075FF)" }}
                >
                  <IoSparkles color="#fff" size="24px" />
                </VuiBox>
                <VuiBox>
                  <VuiTypography variant="lg" color="white" fontWeight="bold">
                    AI Financial Insights
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text">
                    {isAI ? "Powered by DeepSeek AI" : "Based on your spending patterns"}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
              <VuiButton color="info" size="small" onClick={refreshInsights} disabled={loadingInsights}>
                {loadingInsights ? "Analyzing..." : "Refresh Insights"}
              </VuiButton>
            </VuiBox>
          </Card>
        </VuiBox>

        {/* Smart Insights Cards */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            {loadingInsights ? (
              <Grid item xs={12}>
                <Card sx={{ p: 4, textAlign: "center" }}>
                  <IoSparkles color="#7928CA" size="32px" />
                  <VuiTypography variant="button" color="text" mt={2} display="block">
                    Analyzing your financial data...
                  </VuiTypography>
                </Card>
              </Grid>
            ) : (
              insightsList.map((insight, i) => {
                const style = INSIGHT_STYLES[insight.type] || INSIGHT_STYLES.tip;
                return (
                  <Grid item xs={12} sm={6} key={i}>
                    <Card
                      sx={{
                        p: 3, height: "100%",
                        background: style.bg,
                        border: `1px solid ${style.border}`,
                      }}
                    >
                      <VuiBox display="flex" gap="12px">
                        <VuiBox
                          display="flex" justifyContent="center" alignItems="center"
                          sx={{
                            width: "40px", height: "40px", borderRadius: "10px",
                            background: `${style.iconColor}22`, flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: style.iconColor, fontSize: "20px" }}>
                            {insight.icon || "lightbulb"}
                          </Icon>
                        </VuiBox>
                        <VuiBox>
                          <VuiTypography variant="button" color="white" fontWeight="bold" mb="4px">
                            {insight.title}
                          </VuiTypography>
                          <VuiTypography variant="caption" color="text" fontWeight="regular">
                            {insight.description}
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </VuiBox>

        {/* Predictions + Goals */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            {/* Predictions */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiBox display="flex" alignItems="center" gap="8px" mb="20px">
                  <Icon sx={{ color: "#7928CA" }}>auto_awesome</Icon>
                  <VuiTypography variant="lg" color="white" fontWeight="bold">
                    {isAI ? "Next Month Predictions" : "Financial Overview"}
                  </VuiTypography>
                </VuiBox>
                {predictions.length > 0 ? (
                  predictions.map((pred, i) => (
                    <VuiBox
                      key={i}
                      display="flex" justifyContent="space-between" alignItems="center"
                      py={1.5}
                      sx={{ borderBottom: i < predictions.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}
                    >
                      <VuiTypography variant="button" color="text" fontWeight="medium">
                        {pred.label}
                      </VuiTypography>
                      <VuiBox display="flex" alignItems="center" gap="8px">
                        <VuiTypography variant="button" color="white" fontWeight="bold">
                          {pred.value}
                        </VuiTypography>
                        {pred.trend === "up" && <IoTrendingUp color="#E31A1A" size="16px" />}
                        {pred.trend === "down" && <IoTrendingDown color="#01B574" size="16px" />}
                        {pred.trend === "stable" && <IoRemove color="#FFB547" size="16px" />}
                      </VuiBox>
                    </VuiBox>
                  ))
                ) : (
                  <>
                    <VuiBox display="flex" justifyContent="space-between" py={1.5} sx={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <VuiTypography variant="button" color="text">Net This Month</VuiTypography>
                      <VuiTypography variant="button" color={data.net_pkr >= 0 ? "success" : "error"} fontWeight="bold">
                        Rs. {fmt(Math.abs(data.net_pkr))} {data.net_pkr >= 0 ? "surplus" : "deficit"}
                      </VuiTypography>
                    </VuiBox>
                    <VuiBox display="flex" justifyContent="space-between" py={1.5} sx={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <VuiTypography variant="button" color="text">Daily Average</VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="bold">
                        Rs. {fmt(data.daily_avg)}
                      </VuiTypography>
                    </VuiBox>
                    <VuiBox display="flex" justifyContent="space-between" py={1.5}>
                      <VuiTypography variant="button" color="text">Spending Trend</VuiTypography>
                      <VuiTypography variant="button" color={data.spend_change_pct <= 0 ? "success" : "error"} fontWeight="bold">
                        {data.spend_change_pct > 0 ? "+" : ""}{data.spend_change_pct}% vs last month
                      </VuiTypography>
                    </VuiBox>
                  </>
                )}
              </Card>
            </Grid>

            {/* Goals */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="20px">
                  Financial Goals
                </VuiTypography>
                {goals.map((goal, i) => {
                  const pct = goal.target > 0 ? Math.min((goal.current / goal.target) * 100, 100) : 0;
                  const isOverBudget = goal.name.includes("Budget") && goal.current > goal.target;
                  return (
                    <VuiBox key={i} mb={3}>
                      <VuiBox display="flex" justifyContent="space-between" mb="6px">
                        <VuiTypography variant="button" color="white" fontWeight="medium">
                          {goal.name}
                        </VuiTypography>
                        <VuiTypography variant="caption" color={isOverBudget ? "error" : "text"} fontWeight="bold">
                          Rs. {fmt(goal.current)} / Rs. {fmt(goal.target)}
                        </VuiTypography>
                      </VuiBox>
                      <VuiProgress
                        value={pct}
                        color={isOverBudget ? "error" : goal.color}
                        sx={{ background: "#2D2E5F" }}
                      />
                      {isOverBudget && (
                        <VuiTypography variant="caption" color="error" mt="4px">
                          Over by Rs. {fmt(goal.current - goal.target)}
                        </VuiTypography>
                      )}
                    </VuiBox>
                  );
                })}
              </Card>
            </Grid>
          </Grid>
        </VuiBox>

        {/* Balance Trend */}
        <Card sx={{ p: 3 }}>
          <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
            Spending Impact (30 Days)
          </VuiTypography>
          <VuiTypography variant="button" color="text" fontWeight="regular" mb="20px">
            Cumulative spending effect on balance
          </VuiTypography>
          <VuiBox sx={{ height: "280px" }}>
            {balanceData.length > 0 ? (
              <LineChart lineChartData={balanceLineData} lineChartOptions={balanceLineOptions} />
            ) : (
              <VuiTypography color="text" variant="button">No data</VuiTypography>
            )}
          </VuiBox>
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Insights;
